import { useEffect, useState } from "react";
import { Palette, Share2 } from "lucide-react";
import { freshSession } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { Button, useToast } from "@/ui";
import { useTenant } from "@/tenant/TenantProvider";
import { useLanguage } from "@/i18n/language";
import { CounterPosterDesigner } from "@/components/CounterPosterDesigner";

/**
 * The registration QR + poster block — used inside a modal from the org
 * admin's counters table, inside a card on the counter dashboard, and as a
 * full page at `/:orgSlug/counter/qr`. One visual, three homes.
 */
export function QrPosterPanel({
  qrPngUrl,
  registrationUrl,
  counterName,
  counterPhone = null,
  openDesignOnMount = false,
}: {
  qrPngUrl: string;
  registrationUrl: string;
  /** Optional — used to word the native share sheet's title/text. */
  counterName?: string;
  counterPhone?: string | null;
  /** Opens the designer directly when the user chose Poster from the dashboard. */
  openDesignOnMount?: boolean;
}) {
  const toast = useToast();
  const { payload } = useTenant();
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [designerOpen, setDesignerOpen] = useState(openDesignOnMount);
  // The QR image is fetched by the browser directly, so the session is renewed first.
  const [sessionReady, setSessionReady] = useState(false);
  useEffect(() => {
    void freshSession().then(() => setSessionReady(true));
  }, []);
  useEffect(() => {
    if (openDesignOnMount) setDesignerOpen(true);
  }, [openDesignOnMount]);
  // The share sheet must carry the ORG's product name, not ours — a karigar
  // recognises the jeweller they work for, not the platform behind it.
  const appName = payload.branding.appName as string;

  async function copyLink(): Promise<void> {
    try {
      if (!(await copyText(registrationUrl))) throw new Error("Copy failed");
      setCopied(true);
      toast.success("Link copied.");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link. Copy it by hand instead.");
    }
  }

  async function share(): Promise<void> {
    const title = counterName ? `${counterName} — Karigar Registration` : "Karigar Registration";
    const text = counterName
      ? `Register with ${counterName} on ${appName}.`
      : `Register on ${appName}.`;

    // Feature-detect — navigator.share only exists on secure contexts in a
    // subset of browsers (mostly mobile). Everyone else falls back to copy.
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title, text, url: registrationUrl });
      } catch (err) {
        // The user dismissing the native share sheet throws AbortError — not a failure.
        if (err instanceof Error && err.name !== "AbortError") {
          toast.error("Could not share. Copying the link instead.");
          await copyLink();
        }
      }
      return;
    }

    await copyLink();
  }

  const orgName = payload.orgName ?? appName;
  const orgLogoUrl = payload.hasOwnLogo && typeof payload.branding.logo === "string" ? payload.branding.logo : null;
  const brandColor = typeof payload.branding.color1 === "string" && /^#[\da-f]{6}$/i.test(payload.branding.color1)
    ? payload.branding.color1
    : "#C54E1B";
  const brandAccent = typeof payload.branding.color2 === "string" && /^#[\da-f]{6}$/i.test(payload.branding.color2)
    ? payload.branding.color2
    : "#D7A749";

  return (
    <>
    <div className="flex flex-col items-center gap-4">
      {/* bg-white is intentional, not a hardcode to "fix": a QR code needs a
          true white background to stay scannable under any org theme, so it
          is exempt from the token rule. Do not swap this for a brand token. */}
      <div className="rounded-lg border border-rule bg-white p-4">
        {/* Same-origin API route — the browser sends the session cookie automatically. */}
        {sessionReady ? (
          <img alt="Registration QR code" className="size-56" src={qrPngUrl} />
        ) : (
          <div className="size-56 animate-pulse rounded bg-ink/[0.05]" />
        )}
      </div>

      <p className="text-center text-sm text-muted">
        {t("कारीगर पंजीकरण", "Karigar Registration")}
        <br />
        {t("WhatsApp पर रजिस्टर करने के लिए स्कैन करें", "Scan to register on WhatsApp")}
      </p>

      <div className="flex w-full items-center gap-2 rounded-md border border-rule bg-surface px-3 py-2">
        <p className="flex-1 truncate text-sm text-ink" title={registrationUrl}>
          {registrationUrl}
        </p>
        <Button onClick={() => void copyLink()} size="sm" variant="secondary">
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>

      <div className="grid w-full gap-2 sm:grid-cols-2">
        <Button className="w-full" icon={<Palette aria-hidden="true" className="size-4" />} onClick={() => setDesignerOpen(true)} variant="secondary">
          Design & download poster
        </Button>
        <Button className="w-full" icon={<Share2 aria-hidden="true" className="size-4" />} onClick={() => void share()} variant="secondary">
          Share
        </Button>
      </div>
    </div>
    <CounterPosterDesigner
      appName={appName}
      brandAccent={brandAccent}
      brandColor={brandColor}
      counterName={counterName ?? orgName}
      counterPhone={counterPhone}
      onClose={() => setDesignerOpen(false)}
      open={designerOpen}
      orgLogoUrl={orgLogoUrl}
      orgName={orgName}
      qrPngUrl={qrPngUrl}
    />
    </>
  );
}
